import {
  Avatar,
  Box,
  Button,
  Divider,
  Popover,
  Typography,
} from "@mui/material";
import React, { ReactElement, useContext } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { Link } from "react-router";
import { nameInitial } from "@/utils";
import { ProfileMenu } from "@/components";

interface UserTopBoxProps {
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
}

/**
 * UserTopBox component displays user profile in a popover menu.
 */
export const UserTopBox = (props: UserTopBoxProps): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const { profilePhotoUrl } = user || {};

  const open = Boolean(props.anchorEl);

  const handleClose = (): void => {
    if (typeof props.onClose === "function") {
      props.onClose();
    }
  };

  return (
    <Popover
      anchorEl={props.anchorEl || null}
      id="account-menu"
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: 300,
            maxWidth: "100%",
            backgroundColor: "primary.main",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "primary.700",
              borderRadius: "8px",
            },
          },
        },
      }}
      transformOrigin={{
        horizontal: "right",
        vertical: "top",
      }}
      anchorOrigin={{
        horizontal: "right",
        vertical: "bottom",
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          cursor: "auto",
          display: "block",
          color: "primary.100",
          p: 2,
        }}
      >
        <Box sx={{ textAlign: "center" }}>{user?.email}</Box>
        <Box
          sx={{
            p: 2,
            justifyContent: "center",
            display: "flex",
          }}
        >
          <Avatar
            alt={user?.name}
            src={profilePhotoUrl}
            sx={{
              height: 100,
              width: 100,
              borderStyle: "solid",
              borderWidth: "2px",
              borderColor: "primary.100",
            }}
          >
            {nameInitial(user?.name || "")}
          </Avatar>
        </Box>

        <Box>
          <Typography
            variant="h5"
            className="font-weight-bold"
            sx={{ textAlign: "center", color: "primary.100" }}
          >
            {user?.name}
          </Typography>
        </Box>

        <Box
          sx={{
            pt: 2,
            justifyContent: "center",
            display: "flex",
          }}
        >
          <Button
            component={Link}
            to="/user-account/profile"
            variant="outlined"
            sx={{
              borderColor: "primary.100",
              color: "primary.100",
              borderRadius: 20,
              px: 10,
              "&:hover": {
                transform: "translateY(0)",
                borderColor: "primary.50",
                color: "primary.50",
              },
            }}
          >
            {gettext("My Profile")}
          </Button>
        </Box>
      </Box>

      <Divider color="primary.100" />
      {open && (
        <Box
          sx={{
            p: 1,
          }}
        >
          <ProfileMenu />
        </Box>
      )}
    </Popover>
  );
};
